import type { ListParams, PageResult } from '@beonit/adaptive-raindrop';
import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { listQuery } from '@shared/infrastructure/http/list-query.js';
import { type UploadOptions, uploadToStorage } from '@shared/infrastructure/http/upload.js';
import { isControlKind } from '../lib/group-owner.js';
import { labelStateOf } from '../lib/video-labelling.js';
import type {
  AffectedGroup,
  BatchEstimate,
  BulkLaunch,
  DeleteImpact,
  DeleteVariant,
  DeleteVariantOffer,
  LaunchedRun,
  LaunchOptions,
  RegisteredVideo,
  TranscriptionStatus,
  UploadTicket,
  Video,
  VideoEstimate,
  VideoFilters,
  VideoUpload,
} from '../model.js';

/**
 * A video exactly as it arrives from the back-office.
 *
 * snake_case, instants as text — and already normalised: the seven spellings of the
 * duration and the three of the folder were collapsed on the way through Nest, so this
 * gateway has one field per concept to read and no defensive helpers.
 */
interface VideoPayload {
  id: number;
  name: string;
  description: string | null;
  original_filename: string;
  filesize: number | null;
  duration_seconds: number | null;
  status: Video['status'];
  progress: number;
  error_message: string | null;
  folder_id: number | null;
  folder_name: string | null;
  analysis_config_id: number | null;
  active_run_id: string | null;
  favourite?: boolean;
  active_run_pinned: boolean;
  result_doc_id: string | null;
  analysis_started_at: string | null;
  analysis_completed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  preview_image_url: string | null;
  preview_video_url: string | null;
  /** `incoherent`, `coherent` or null: the state of its incoherence labelling. */
  label: string | null;
  /** How many incoherence ranges it has. Absent from a gateway older than the ranges. */
  incoherence_ranges?: number | null;
  /** The video's own transcription job: `none` when it was never asked for. */
  transcription_status?: string | null;
  /** Absent from a gateway older than the persons. */
  person?: { id: number; name: string } | null;
}

interface DeleteImpactPayload {
  source_id: number;
  groups: Array<{
    group_id: number | null;
    /** A control the video is in, by its kind; absent from a gateway older than the controls. */
    control_kind?: string | null;
    name: string;
    has_executions: boolean;
    excluded_from_metrics: boolean;
    snapshots_affected: number;
  }>;
  touches_executed_group: boolean;
  runs_total: number;
  runs_completed: number;
  labelled: boolean;
  /** Absent from a gateway older than 2026-10-29. */
  gesture_labels?: number | null;
  snapshots_affected: number;
  variants: Array<{
    variant: DeleteVariant;
    file_deleted: boolean;
    analyses_deleted: boolean;
    counts_in_metrics: boolean;
    changes_past_metrics: boolean;
    available: boolean;
    unavailable_reason: string | null;
  }>;
}

interface EstimatePayload {
  videos: Array<{
    source_id: string;
    estimated_ms: number | null;
    basis: VideoEstimate['basis'];
    samples: number;
    confident: boolean;
  }>;
  total_ms: number | null;
  unknown_videos: number;
  confident: boolean;
}

interface LaunchedRunPayload {
  run_id: string;
  engines: string[];
  engines_auto_added: string[];
  position: number | null;
}

interface BulkLaunchPayload {
  batch: string;
  accepted: Array<{ id: number; run_id: string; position: number | null }>;
  rejected: Array<{ id: number; reason: string }>;
}

function toLaunchedRun(payload: LaunchedRunPayload): LaunchedRun {
  return {
    runId: payload.run_id,
    engines: payload.engines ?? [],
    enginesAutoAdded: payload.engines_auto_added ?? [],
    position: payload.position ?? null,
  };
}

/**
 * The launch body, with only the fields that carry a decision.
 *
 * `analysisConfigId` is dropped unless it was given, never sent as null: its mere presence rewrites
 * the video's configuration upstream. `promoteOnSuccess` always travels, `false` included, because
 * it is the one field whose default nobody should be relying on.
 */
function launchBody(options: LaunchOptions): Record<string, unknown> {
  const body: Record<string, unknown> = {
    engines: [...options.engines],
    promoteOnSuccess: options.promoteOnSuccess,
  };

  if (options.analysisConfigId !== undefined) body.analysisConfigId = options.analysisConfigId;
  if (options.configVersionId) body.configVersionId = options.configVersionId;
  if (options.batch?.trim()) body.batch = options.batch.trim();
  if (options.note?.trim()) body.note = options.note.trim();

  return body;
}

const TRANSCRIPTION_STATUSES: ReadonlySet<string> = new Set<TranscriptionStatus>([
  'none',
  'pending',
  'running',
  'done',
  'failed',
]);

/** A status this build does not know reads as unknown, never as one it happens to resemble. */
function toTranscriptionStatus(value: string | null | undefined): TranscriptionStatus | null {
  return typeof value === 'string' && TRANSCRIPTION_STATUSES.has(value)
    ? (value as TranscriptionStatus)
    : null;
}

/** The one place a date is born on this side of the module. */
function date(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

function toVideo(payload: VideoPayload): Video {
  return {
    id: payload.id,
    name: payload.name,
    description: payload.description,
    originalFilename: payload.original_filename,
    filesizeBytes: payload.filesize,
    durationSeconds: payload.duration_seconds,
    status: payload.status,
    progress: payload.progress,
    errorMessage: payload.error_message,
    folderId: payload.folder_id,
    folderName: payload.folder_name,
    analysisConfigId: payload.analysis_config_id,
    activeRunId: payload.active_run_id,
    favourite: payload.favourite === true,
    resultId: payload.result_doc_id,
    analysisStartedAt: date(payload.analysis_started_at),
    analysisCompletedAt: date(payload.analysis_completed_at),
    createdAt: date(payload.created_at),
    updatedAt: date(payload.updated_at),
    thumbnailUrl: payload.preview_image_url,
    videoUrl: payload.preview_video_url,
    label: labelStateOf(payload.label),
    incoherenceRanges: rangesOf(payload),
    transcriptionStatus: toTranscriptionStatus(payload.transcription_status),
    person: payload.person ? { id: payload.person.id, name: payload.person.name } : null,
  };
}

/** An incoherent video has at least one range, whatever a missing count says. */
function rangesOf(payload: VideoPayload): number {
  const count = Number(payload.incoherence_ranges);
  const known = Number.isInteger(count) && count > 0 ? count : 0;

  return labelStateOf(payload.label) === 'incoherent' ? Math.max(known, 1) : 0;
}

function toGroup(payload: DeleteImpactPayload['groups'][number]): AffectedGroup {
  return {
    groupId: isControlKind(payload.control_kind) ? null : payload.group_id,
    controlKind: isControlKind(payload.control_kind) ? payload.control_kind : null,
    name: payload.name,
    hasExecutions: payload.has_executions === true,
    excludedFromMetrics: payload.excluded_from_metrics,
    snapshotsAffected: payload.snapshots_affected,
  };
}

function toOffer(payload: DeleteImpactPayload['variants'][number]): DeleteVariantOffer {
  return {
    variant: payload.variant,
    fileDeleted: payload.file_deleted,
    analysesDeleted: payload.analyses_deleted,
    countsInMetrics: payload.counts_in_metrics,
    changesPastMetrics: payload.changes_past_metrics,
    available: payload.available,
    unavailableReason: payload.unavailable_reason,
  };
}

function toImpact(payload: DeleteImpactPayload): DeleteImpact {
  return {
    videoId: payload.source_id,
    groups: (payload.groups ?? []).map(toGroup),
    touchesExecutedGroup: payload.touches_executed_group === true,
    runsTotal: payload.runs_total,
    runsCompleted: payload.runs_completed,
    labelled: payload.labelled,
    gestureLabels: typeof payload.gesture_labels === 'number' ? payload.gesture_labels : 0,
    snapshotsAffected: payload.snapshots_affected,
    variants: (payload.variants ?? []).map(toOffer),
  };
}

function toEstimate(payload: EstimatePayload): BatchEstimate {
  return {
    videos: (payload.videos ?? []).map((video) => ({
      videoId: video.source_id,
      estimatedMs: video.estimated_ms,
      basis: video.basis,
      samples: video.samples,
      confident: video.confident,
    })),
    totalMs: payload.total_ms,
    unknownVideos: payload.unknown_videos,
    confident: payload.confident,
  };
}

/** The largest page the back-office API accepts (`MAX_PAGE_SIZE`). */
const ALL_PAGE_SIZE = 200;

/**
 * The gallery's filters, as the query the back-office reads.
 *
 * Only what was asked for travels: an empty list or an absent bound sends nothing, while a zero is
 * a bound and goes. The order is not here — it is `orderBy`/`orderDir` on the list params, like
 * every other list of the back-office.
 */
function filterQuery(filters: VideoFilters): Record<string, unknown> {
  return {
    // `null` is the filter for «no folder» and has to survive as a word: an empty
    // value in a query string cannot be told apart from «do not filter».
    folderId: filters.folderId === null ? 'unassigned' : filters.folderId,
    groupId: filters.groupId,
    notInGroupId: filters.notInGroupId,
    controlKind: filters.controlKind,
    notInControlKind: filters.notInControlKind,
    // Two parameters, never both: a person, or the flag for the videos of nobody.
    personId: filters.personId ?? undefined,
    withoutPerson: filters.personId === null ? true : undefined,
    status: filters.statuses,
    label: filters.labels,
    configId: filters.configId,
    createdFrom: filters.createdFrom,
    createdTo: filters.createdTo,
    durationMin: filters.durationMin,
    durationMax: filters.durationMax,
  };
}

/** The most videos one bulk delete takes: the back-office's cap. */
const BULK_DELETE_MAX = 200;

/** What deleting several videos answers: `refused` carries upstream's code and sentence. */
export interface BulkVideoDeletion {
  deleted: number[];
  refused: Array<{ id: number; code: string | null; reason: string }>;
}

/**
 * Boundary with the video dataset.
 *
 * Every path goes through the back-office, except one: the file itself, which the browser
 * PUTs straight to the storage with a signed URL. That is not a shortcut around the
 * gateway — the gateway is what hands out the URL, and it is the only way a multi-gigabyte
 * file does not travel through Nest twice.
 */
export const videosApi = {
  list: async (params: ListParams, filters: VideoFilters = {}): Promise<PageResult<Video>> => {
    const page = await httpClient.getPage<VideoPayload>(
      `/virtual-profiler/videos${listQuery(params, filterQuery(filters))}`,
    );

    return { items: page.items.map(toVideo), meta: page.meta };
  },

  /**
   * Every video the search and the filters match, across all the pages.
   *
   * For «seleccionar todos los que coinciden»: the gallery shows twenty-four at a time, and a
   * selection meant to be the whole filtered set would otherwise be one page of it. Whole rows and
   * not just ids, because deleting a selection needs each video's name and impact, and a selection
   * resolved against the page on screen would delete twenty-four of three hundred. Walked at the
   * largest page the API takes, and stopped on an empty page too, so a total that grows or shrinks
   * underneath cannot turn it into a loop.
   */
  allMatching: async (search: string | undefined, filters: VideoFilters = {}): Promise<Video[]> => {
    const videos: Video[] = [];

    for (let page = 1; ; page += 1) {
      const answer = await httpClient.getPage<VideoPayload>(
        `/virtual-profiler/videos${listQuery({ page, pageSize: ALL_PAGE_SIZE, search }, filterQuery(filters))}`,
      );

      videos.push(...answer.items.map(toVideo));

      if (answer.items.length === 0 || page >= answer.meta.totalPages) return videos;
    }
  },

  get: async (videoId: number): Promise<Video> =>
    toVideo(await httpClient.get<VideoPayload>(`/virtual-profiler/videos/${videoId}`)),

  /**
   * Renames a video.
   *
   * The id is not part of what changes, which is the promise of HU-18: runs, labels and
   * group memberships keep pointing at the same video.
   *
   * `description: null` clears it; leaving it out leaves it alone. The two are different
   * instructions all the way down to Virtual Profiler.
   */
  rename: async (
    videoId: number,
    patch: { name?: string; description?: string | null },
  ): Promise<Video> =>
    toVideo(await httpClient.put<VideoPayload>(`/virtual-profiler/videos/${videoId}`, patch)),

  /**
   * Marks or unmarks a video as a favourite.
   *
   * The state it should end in, not a toggle: two stars pressed at once then agree on the answer
   * instead of cancelling each other out.
   */
  setFavourite: async (videoId: number, favourite: boolean): Promise<Video> =>
    toVideo(
      await httpClient.put<VideoPayload>(`/virtual-profiler/videos/${videoId}/favourite`, {
        favourite,
      }),
    ),

  /** What deleting this video would cost. Read before the dialog, never after. */
  deleteImpact: async (videoId: number): Promise<DeleteImpact> =>
    toImpact(
      await httpClient.get<DeleteImpactPayload>(
        `/virtual-profiler/videos/${videoId}/delete-impact`,
      ),
    ),

  /**
   * Deletes a video the chosen way.
   *
   * The variant is a required argument with no default here either. A default would mean
   * one of three consequences happening because a caller left an argument out, and one of
   * them destroys analyses.
   */
  remove: (videoId: number, variant: DeleteVariant): Promise<void> =>
    httpClient.post<void>(`/virtual-profiler/videos/${videoId}/delete?variant=${variant}`),

  /**
   * Several videos deleted in one call (2026-10-29), the one way there is. A refusal is that
   * video's own and the rest still go; each group touched gets one dataset version for all.
   */
  removeMany: async (videoIds: readonly number[]): Promise<BulkVideoDeletion> => {
    const result: BulkVideoDeletion = { deleted: [], refused: [] };

    // One call per `BULK_DELETE_MAX` (the back-office's cap): any selection on screen fits in one.
    for (let start = 0; start < videoIds.length; start += BULK_DELETE_MAX) {
      const answer = await httpClient.post<{
        deleted?: number[];
        refused?: Array<{ id: number; code: string | null; reason: string }>;
      }>('/virtual-profiler/videos/bulk-delete', {
        videoIds: videoIds.slice(start, start + BULK_DELETE_MAX),
      });
      result.deleted.push(...(answer?.deleted ?? []));
      result.refused.push(...(answer?.refused ?? []));
    }

    return result;
  },

  uploadTicket: async (file: File): Promise<UploadTicket> => {
    const ticket = await httpClient.post<{
      url: string;
      key: string;
      expires_in: number;
    }>('/virtual-profiler/videos/upload-ticket', {
      filename: file.name,
      contentType: file.type,
      filesize: file.size,
    });

    return {
      url: ticket.url,
      key: ticket.key,
      expiresInSeconds: ticket.expires_in,
    };
  },

  /**
   * Sends the bytes straight to the storage.
   *
   * Takes the progress callback and the abort signal rather than hiding them: a file of
   * several gigabytes behind a spinner is indistinguishable from a hung upload, and
   * somebody will reload the page halfway through.
   *
   * The content type is the file's own, which is what the ticket was signed for. A
   * mismatch is refused by the storage with a signature error that mentions no content
   * type at all.
   */
  putFile: (ticket: UploadTicket, file: File, options: UploadOptions = {}): Promise<void> =>
    uploadToStorage(ticket.url, file, file.type, options),

  /**
   * Adds an uploaded object to the dataset (product owner, 2026-09-28).
   *
   * Registering launches nothing: no configuration and no engines travel, and the answer is the
   * video's id. Upstream asks for the video's transcription on its own; analysing it is a group
   * execution's business. No group travels either — joining a group is a second call, after this
   * one.
   */
  register: async (upload: VideoUpload): Promise<RegisteredVideo> => {
    const registered = await httpClient.post<{ id: number }>('/virtual-profiler/videos/register', {
      videoKey: upload.videoKey,
      filename: upload.filename,
      filesize: upload.filesizeBytes,
      name: upload.name,
      description: upload.description,
      folderId: upload.folderId,
    });

    return { id: registered.id };
  },

  /**
   * Launches a new analysis of one video. Costs money when coherence is among the engines.
   *
   * The only way to put coherence back on a video that already has a result. It appends a run to
   * the ledger; nothing is erased.
   */
  reanalyze: async (videoId: number, options: LaunchOptions): Promise<LaunchedRun> =>
    toLaunchedRun(
      await httpClient.post<LaunchedRunPayload>(
        `/virtual-profiler/videos/${videoId}/reanalyze`,
        launchBody(options),
      ),
    ),

  /**
   * Launches the same analysis over many videos, under one batch.
   *
   * A video that cannot take the run comes back in `rejected` and never fails the call, so the
   * answer has to be read even when the promise resolves.
   */
  bulkReanalyze: async (
    videoIds: readonly number[],
    options: LaunchOptions,
  ): Promise<BulkLaunch> => {
    const answer = await httpClient.post<BulkLaunchPayload>('/virtual-profiler/videos/reanalyze', {
      videoIds: [...new Set(videoIds)],
      ...launchBody(options),
    });

    return {
      batch: answer.batch,
      accepted: (answer.accepted ?? []).map((run) => ({
        videoId: run.id,
        runId: run.run_id,
        position: run.position ?? null,
      })),
      rejected: (answer.rejected ?? []).map((refusal) => ({
        videoId: refusal.id,
        reason: refusal.reason,
      })),
    };
  },

  /** How long re-analysing these videos would take. A POST because the id list can be long. */
  estimate: async (
    videoIds: readonly number[],
    engines: readonly string[] = [],
  ): Promise<BatchEstimate> =>
    toEstimate(
      await httpClient.post<EstimatePayload>('/virtual-profiler/videos/estimate', {
        videoIds: [...videoIds],
        engines: [...engines],
      }),
    ),
};
